import { getEnvironment, UprightEnvironment } from '@upright/common/environment';
import { useGetLoggedInUser, useIsTeam } from '@upright/ui/common/generic/hooks';
import { isStaffUser } from '@upright/common/utils';
import { withEmptyFrameNoPadding } from '@upright/ui/views/internal/with-empty-frame';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorPage } from '../../../error-view';
import { AppError } from '../../../../common/generic/components/app-error';
import { BouAppLoading } from '../../../../common/generic/components/progress/spinners/bou-app-loading';
import { BouToolPageLoading } from '../../../../common/generic/components/progress/spinners/bou-page-content-loading';
import { HomeViewFrame } from '../../../home-views/home-view-frame';
import { UnifiedShellFrame, useInUnifiedShellFrame } from '../../../unified-shell/unified-shell-frame';
import { useClaimUnifiedShellFrame } from '../../../unified-shell/unified-shell-frame-claim';
import { useUnifiedShellFrameActive } from '../../../unified-shell/use-unified-shell-frame-active';
import { useUnifiedShell } from '../../../unified-shell/use-unified-shell';
import { isAccessProfilePath, isUnifiedEntityFramePath } from '../../../unified-shell/company-profile-route';
import { isFullMaintenanceMode, useMaintenanceModeText } from '../../context/use-is-maintenance-mode';
import { clientInputRoutes } from './route-declarations/client-input-routes';
import { contentRoutes } from './route-declarations/content-routes';
import { getEntityViewRoutes } from './route-declarations/entity-view-routes';
import { homeViewRoutes } from './route-declarations/home-view-routes';
import { eevoXBotDecisionRoutes, internalRoutes } from './route-declarations/internal-routes';
import { otherRoutes } from './route-declarations/other-routes';
import { getRedirectRoutes } from './route-declarations/redirect-routes';
import { getRoutes } from './route-utils';
import { useShouldShowHomeViewFrame, useShowLoggedInHomeView } from './use-should-show-home-view-frame';

const MaintenanceModeView = lazy(async () => await import('../../../maintenance-view'));
const LanderView = lazy(async () => await import('../../../home-lander-view'));
const LoggedInHomeViewSwitch = lazy(async () => await import('../../../home-views/'));
const NiIPlaygroundView = withEmptyFrameNoPadding(lazy(async () => await import('@upright/ui/views/internal/ni2')));

export function RoutesMaybe() {
  const location = useLocation();
  const maintenanceModeText = useMaintenanceModeText();
  const disableNormalRoutes = maintenanceModeText ? isFullMaintenanceMode(maintenanceModeText) : false;
  const showLoggedInHomeView = useShowLoggedInHomeView();
  const showHomeViewFrame = useShouldShowHomeViewFrame(showLoggedInHomeView);
  // The unified shell's frame also wraps the chat surface (PLT-6610), which is not a home-view
  // route. `loading` counts as a reason to take the frame branch: `HomeFrame` holds the page
  // while a customer's opt-in resolves, instead of the chat's own full-screen frame being built
  // and then replaced.
  const unifiedShellFrame = useUnifiedShellFrameActive();
  const unifiedShell = useUnifiedShell();
  const showInternalRoutes = useIsTeam();
  const showEevoXBotDecisionRoutes = isStaffUser(useGetLoggedInUser());
  // Only the team has the access route, so only a team reader's access path takes the profile frame.
  const companyProfileFrame =
    unifiedShell.enabled &&
    (isUnifiedEntityFramePath(location.pathname) || (showInternalRoutes && isAccessProfilePath(location.pathname)));
  const showFrame = showHomeViewFrame || unifiedShellFrame.active || unifiedShellFrame.loading;
  const isDev = getEnvironment() === UprightEnvironment.DEVELOPMENT;

  if (disableNormalRoutes) {
    return <MaintenanceModeView text={maintenanceModeText || ''} />;
  }

  return (
    <AppError pathname={location.pathname}>
      <Suspense
        fallback={
          showFrame ? (
            <HomeFrame suspended />
          ) : companyProfileFrame ? (
            <UnifiedShellFrame suspended />
          ) : (
            <BouToolPageLoading hideSideBar />
          )
        }
      >
        <Routes>
          <Route path="/" element={showFrame ? <HomeFrame /> : <Outlet />}>
            <Route key="main" index element={showLoggedInHomeView ? <LoggedInHomeViewSwitch /> : <LanderView />} />
            <Route path="/main" element={<LanderView />} />
            {showHomeViewFrame && getRoutes(homeViewRoutes)}
            {getRoutes(clientInputRoutes)}
            {getEntityViewRoutes()}
            {getRoutes(otherRoutes)}
            {getRoutes(contentRoutes)}
            {getRedirectRoutes()}
            <Route
              path={'internal'}
              element={
                showInternalRoutes || showEevoXBotDecisionRoutes ? (
                  <InternalRoutesOutlet />
                ) : (
                  <InternalRouteDisclaimer />
                )
              }
            >
              {showInternalRoutes && getRoutes(internalRoutes)}
              {showEevoXBotDecisionRoutes && getRoutes(eevoXBotDecisionRoutes)}
            </Route>
            {isDev && <Route path="/secret-ni2-playground" element={<NiIPlaygroundView />} />}
            {/* The home-view routes are only registered for logged-in clients and team, so a
                signed-out visitor's deep link to one lands here; send them to the lander rather
                than a "Page not found" for a page that exists. */}
            <Route path={'*'} element={showLoggedInHomeView ? <PageNotFoundRoute /> : <Navigate to={'/'} />} />
          </Route>
        </Routes>
      </Suspense>
    </AppError>
  );
}

/**
 * The frame around the home-view routes. `useUnifiedShellFrameActive` says which shell the
 * user is in — the unified shell's frame with its own white bar, or the legacy frame under the
 * dark header — and the header switch reads the same hook, so the two cannot disagree. While a
 * customer's opt-in is still loading neither frame renders: the app spinner holds the page
 * instead of the dark chrome being painted and swapped for the white a moment later.
 */
function HomeFrame(props: { suspended?: boolean }) {
  const frame = useUnifiedShellFrameActive();
  if (frame.loading) {
    return <BouAppLoading />;
  }
  return frame.active ? (
    <UnifiedShellFrame suspended={props.suspended} />
  ) : (
    <HomeViewFrame suspended={props.suspended} />
  );
}

/**
 * "Page not found" for a URL no route matches. An opted-in user gets it inside the unified shell's
 * frame at every width, the same as a missing company or portfolio; everyone else gets the legacy
 * page frame.
 *
 * The frame is this route's own, because the frame routes are chosen by path and an unknown path
 * matches none of them. Under a path the frame already wraps (an unknown sub-path of a home view)
 * the page renders in that frame instead of a second one. The claim tells the header to leave the
 * bar to this frame: the header cannot see that no route matched, and would otherwise paint its own
 * bar on top, which below tablet is the legacy `MobileHeader`.
 */
function PageNotFoundRoute() {
  const shell = useUnifiedShell();
  const inFrame = useInUnifiedShellFrame();
  const ownFrame = shell.enabled && !inFrame;
  useClaimUnifiedShellFrame(ownFrame);
  if (shell.loading && !inFrame) {
    return <BouAppLoading />;
  }
  const page = <ErrorPage error={{ status: 410, statusText: '' }} pageType="generic-route" />;
  return ownFrame ? <UnifiedShellFrame>{page}</UnifiedShellFrame> : page;
}

const InternalRouteDisclaimer = () => (
  <div style={{ margin: '2em' }}>This is an Upright internal feature. Please log in with an Upright team account.</div>
);

/**
 * PLT-4774: internal admin views that need the slim hierarchy
 * (products, taxonomy visualization, constraint sets, comparisons, ML
 * samples, etc.) call `useProductsByCode()` directly. React Query
 * dedupes per query key, so a single fetch covers all consumers
 * mounted under the same route tree.
 */
const InternalRoutesOutlet = () => {
  return <Outlet />;
};
